import { cn } from "../../lib/cn";

/**
 * The article text measure as an absolute length: 30em of the paragraph size
 * (text-base below tablet, text-lg above). --measure-reading is in em and
 * would resolve per element, so headings, figures and the hero image read
 * this instead to share the paragraphs' edges.
 */
export const articleMeasureClassName =
  "[--article-measure:30rem] tablet:[--article-measure:33.75rem]";

/** Shared MDX article typography — safe for server and client components. */
export const articleProseClassName = cn(
  "prose prose-lg dark:prose-invert max-w-none",
  "prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight",
  "prose-h2:text-2xl prose-h2:tablet:text-3xl prose-h2:mt-12 prose-h2:mb-6",
  "prose-h3:text-xl prose-h3:tablet:text-2xl prose-h3:mt-10 prose-h3:mb-4",
  "prose-h4:text-lg prose-h4:tablet:text-xl prose-h4:mt-8 prose-h4:mb-3",
  "prose-p:font-body prose-p:text-base prose-p:tablet:text-lg",
  "prose-p:leading-relaxed prose-p:text-foreground/90",
  "prose-p:my-6",
  // One centred axis. Text blocks sit at the reading measure (30em of the
  // paragraph size: text-base below tablet, text-lg above), centred in the
  // column, so headings, lists, quotes, code and centre figures share the
  // paragraphs' edges; figures cap themselves at the same measure.
  // Embedded components (not-prose, e.g. StageList) and tables keep the full
  // column and their own layouts. The centring is important: embedded
  // components reset their own inline margins in unlayered CSS.
  articleMeasureClassName,
  "[&>*]:!mx-auto [&>*:not(.not-prose):not(table)]:max-w-[var(--article-measure)]",
  "prose-a:text-primary prose-a:font-medium prose-a:underline",
  "prose-a:underline-offset-2 hover:prose-a:text-primary/80",
  "prose-li:font-body prose-li:text-base prose-li:tablet:text-lg",
  "prose-li:text-foreground/90",
  "prose-ul:my-6 prose-ul:list-disc",
  "prose-ol:my-6 prose-ol:list-decimal",
  "prose-li:my-2",
  "prose-li:marker:text-primary",
  "prose-blockquote:font-body prose-blockquote:italic",
  "prose-blockquote:border-l-4 prose-blockquote:border-primary",
  "prose-blockquote:pl-6 prose-blockquote:py-2",
  "prose-blockquote:text-lg prose-blockquote:tablet:text-xl",
  "prose-blockquote:text-muted-foreground",
  "prose-blockquote:my-8",
  "prose-code:font-mono",
  "prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded",
  // Typography's default wraps inline code in literal backticks via
  // ::before/::after; the chip background already marks it as code.
  "prose-code:before:content-none prose-code:after:content-none",
  "prose-pre:bg-muted prose-pre:rounded-lg prose-pre:p-4",
  "prose-pre:max-h-[35vh]",
  "prose-pre:overflow-x-hidden prose-pre:overflow-y-auto prose-pre:whitespace-pre-wrap prose-pre:break-words prose-pre:my-6",
  "prose-img:rounded-lg",
  "prose-figure:my-0",
  "prose-figcaption:mt-0",
  "prose-table:my-6",
  "prose-th:font-body prose-th:font-semibold prose-th:text-left",
  "prose-td:font-body",
  "prose-strong:font-semibold prose-strong:text-foreground",
  "prose-em:italic",
  "prose-hr:my-12 prose-hr:border-border",
);
